import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import * as notificationService from 'features/Notifications/services/notificationService'
import type { NotificationItem, NotificationPage } from 'features/Notifications/types/notification.types'

/**
 * The bell (MYD-542): how many notifications are unread, and the list once it
 * has been opened.
 *
 * The count is polled (useUnreadNotifications); the list is fetched only when
 * the bell opens, so a page that never opens it costs one tiny request a
 * minute. Marking read is optimistic and put back if the server refuses.
 *
 * Cleared on logout by the root reducer, like every other slice.
 */

const SLICE_NAME = 'notifications'

export type NotificationListStatus = 'idle' | 'loading' | 'loaded' | 'failed'

interface NotificationSliceState {
	unreadCount: number
	items: NotificationItem[]
	nextCursor: string | null
	status: NotificationListStatus
	loadingMore: boolean
	error: string | null
	/** Ids marked read optimistically, so a refused save can be put back. */
	pendingReads: string[]
	/** What "Mark all read" changed, for the same reason. */
	markAllUndo: { ids: string[]; unreadCount: number } | null
}

const initialState: NotificationSliceState = {
	unreadCount: 0,
	items: [],
	nextCursor: null,
	status: 'idle',
	loadingMore: false,
	error: null,
	pendingReads: [],
	markAllUndo: null,
}

const messageOf = (error: any, fallback: string) => error?.response?.data?.message || fallback

export const fetchUnreadCount = createAsyncThunk(`${SLICE_NAME}/fetchUnreadCount`, async (_: void, { rejectWithValue }) => {
	try {
		return await notificationService.getUnreadCount()
	} catch (error) {
		return rejectWithValue(messageOf(error, 'Could not check for notifications.'))
	}
})

/** The first page, replacing whatever was loaded. */
export const fetchNotifications = createAsyncThunk(`${SLICE_NAME}/fetchNotifications`, async (_: void, { rejectWithValue }) => {
	try {
		return await notificationService.listNotifications()
	} catch (error) {
		return rejectWithValue(messageOf(error, 'Notifications could not be loaded.'))
	}
})

export const fetchMoreNotifications = createAsyncThunk<NotificationPage, void, { state: any; rejectValue: string }>(
	`${SLICE_NAME}/fetchMoreNotifications`,
	async (_, { getState, rejectWithValue }) => {
		try {
			return await notificationService.listNotifications(getState().notifications.nextCursor)
		} catch (error) {
			return rejectWithValue(messageOf(error, 'More notifications could not be loaded.'))
		}
	},
)

export const markNotificationRead = createAsyncThunk(`${SLICE_NAME}/markNotificationRead`, async (id: string, { rejectWithValue }) => {
	try {
		return await notificationService.markNotificationRead(id, true)
	} catch (error) {
		return rejectWithValue(messageOf(error, 'Could not mark that notification read.'))
	}
})

export const markAllNotificationsRead = createAsyncThunk(`${SLICE_NAME}/markAllNotificationsRead`, async (_: void, { rejectWithValue }) => {
	try {
		return await notificationService.markAllNotificationsRead()
	} catch (error) {
		return rejectWithValue(messageOf(error, 'Could not mark notifications read.'))
	}
})

const notificationSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder
			.addCase(fetchUnreadCount.fulfilled, (state, action) => {
				state.unreadCount = action.payload
			})

			.addCase(fetchNotifications.pending, state => {
				state.status = 'loading'
				state.error = null
			})
			.addCase(fetchNotifications.fulfilled, (state, action) => {
				state.status = 'loaded'
				state.items = action.payload.items
				state.nextCursor = action.payload.nextCursor
				state.unreadCount = action.payload.unreadCount
			})
			.addCase(fetchNotifications.rejected, (state, action) => {
				state.status = 'failed'
				state.error = (action.payload as string) ?? 'Notifications could not be loaded.'
			})

			.addCase(fetchMoreNotifications.pending, state => {
				state.loadingMore = true
				state.error = null
			})
			.addCase(fetchMoreNotifications.fulfilled, (state, action) => {
				state.loadingMore = false
				const known = new Set(state.items.map(item => item.id))
				state.items.push(...action.payload.items.filter(item => !known.has(item.id)))
				state.nextCursor = action.payload.nextCursor
				state.unreadCount = action.payload.unreadCount
			})
			.addCase(fetchMoreNotifications.rejected, (state, action) => {
				state.loadingMore = false
				state.error = action.payload ?? 'More notifications could not be loaded.'
			})

			// Optimistic: the dot clears and the count drops at once.
			.addCase(markNotificationRead.pending, (state, action) => {
				const item = state.items.find(i => i.id === action.meta.arg)
				if (item && !item.readAt) {
					item.readAt = new Date().toISOString()
					state.unreadCount = Math.max(0, state.unreadCount - 1)
					state.pendingReads.push(item.id)
				}
			})
			.addCase(markNotificationRead.fulfilled, (state, action) => {
				state.pendingReads = state.pendingReads.filter(id => id !== action.meta.arg)
				const item = state.items.find(i => i.id === action.meta.arg)
				if (item) item.readAt = action.payload.readAt
			})
			.addCase(markNotificationRead.rejected, (state, action) => {
				if (!state.pendingReads.includes(action.meta.arg)) return
				state.pendingReads = state.pendingReads.filter(id => id !== action.meta.arg)
				const item = state.items.find(i => i.id === action.meta.arg)
				if (item) item.readAt = null
				state.unreadCount += 1
			})

			.addCase(markAllNotificationsRead.pending, state => {
				const now = new Date().toISOString()
				const ids = state.items.filter(item => !item.readAt).map(item => item.id)
				state.markAllUndo = { ids, unreadCount: state.unreadCount }
				state.items.forEach(item => {
					if (!item.readAt) item.readAt = now
				})
				state.unreadCount = 0
			})
			.addCase(markAllNotificationsRead.fulfilled, state => {
				state.markAllUndo = null
			})
			.addCase(markAllNotificationsRead.rejected, state => {
				const undo = state.markAllUndo
				if (!undo) return
				state.items.forEach(item => {
					if (undo.ids.includes(item.id)) item.readAt = null
				})
				state.unreadCount = undo.unreadCount
				state.markAllUndo = null
			})
	},
})

export const selectUnreadNotificationCount = (state: any): number => state.notifications?.unreadCount ?? 0

export default notificationSlice
